import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export interface PolicyFormTableColumn {
    id: string;
    title: string;
    type: "text" | "number" | "date" | "radio" | "select" | "textselect";
    required?: boolean;
    placeholder?: string;
    options?: { id: string; value: string }[];
    width?: string;
}

interface TableQuestionBuilderProps {
    columns: PolicyFormTableColumn[];
    setColumns: (cols: PolicyFormTableColumn[]) => void;
    minRows: number | "";
    setMinRows: (rows: number | "") => void;
    maxRows: number | "";
    setMaxRows: (rows: number | "") => void;
}

const QUESTION_TYPES: PolicyFormTableColumn["type"][] = [
    "text",
    "number",
    "date",
    "radio",
    "select",
    "textselect",
];

export const AddTableRow: React.FC<TableQuestionBuilderProps> = ({
    columns,
    setColumns,
    minRows,
    setMinRows,
    maxRows,
    setMaxRows,
}) => {
    const [columnNewOptions, setColumnNewOptions] = useState<Record<
        number,
        { id: string; value: string }
    >>({});

    // Column handlers
    const handleAddColumn = () => {
        const newCol: PolicyFormTableColumn = {
            id: crypto.randomUUID(),
            title: "",
            type: "text",
            options: [],
        };
        setColumns([...columns, newCol]);
    };

    const handleRemoveColumn = (idx: number) => {
        setColumns(columns.filter((_, i) => i !== idx));
        setColumnNewOptions((prev) => {
            const copy = { ...prev };
            delete copy[idx];
            return copy;
        });
    };

    const handleColumnChange = (
        idx: number,
        updates: Partial<PolicyFormTableColumn>
    ) => {
        const updated = [...columns];
        updated[idx] = { ...updated[idx], ...updates };
        setColumns(updated);
    };

    // Option handlers
    const handleColumnOptionChange = (
        colIdx: number,
        optIdx: number,
        updates: { id?: string; value?: string }
    ) => {
        const updated = [...columns];
        const col = updated[colIdx];
        if (!col.options) col.options = [];
        col.options[optIdx] = { ...col.options[optIdx], ...updates };
        setColumns(updated);
    };

    const handleRemoveColumnOption = (colIdx: number, optIdx: number) => {
        const updated = [...columns];
        updated[colIdx].options = updated[colIdx].options?.filter(
            (_, i) => i !== optIdx
        );
        setColumns(updated);
    };

    const handleColumnNewOptionChange = (
        colIdx: number,
        updates: { id?: string; value?: string }
    ) => {
        setColumnNewOptions((prev) => ({
            ...prev,
            [colIdx]: { ...prev[colIdx], ...updates },
        }));
    };

    const handleAddColumnOption = (colIdx: number) => {
        const newOpt = columnNewOptions[colIdx];
        if (!newOpt?.id || !newOpt?.value) return;

        const updated = [...columns];
        const col = updated[colIdx];
        if (!col.options) col.options = [];
        col.options.push(newOpt);
        setColumns(updated);

        setColumnNewOptions((prev) => ({ ...prev, [colIdx]: { id: "", value: "" } }));
    };

    return (
        <div className="space-y-4 border-t pt-4">
            <div className="flex gap-4 items-center">
                <div className="flex flex-col">
                    <label className="text-xs font-medium">Min Rows</label>
                    <input
                        type="number"
                        value={minRows}
                        onChange={(e) => {
                            const value = e.target.value ? parseInt(e.target.value) : "";
                            if (typeof value === 'number' && typeof maxRows === 'number' && value > maxRows) {
                                return;
                            }
                            setMinRows(value);
                        }}
                        placeholder="Min Rows"
                        className="text-sm border rounded px-2 py-1 w-24"
                    />
                </div>
                <div className="flex flex-col">
                    <label className="text-xs font-medium">Max Rows</label>
                    <input
                        type="number"
                        value={maxRows}
                        onChange={(e) => {
                            const value = e.target.value ? parseInt(e.target.value) : "";
                            if (typeof value === 'number' && typeof maxRows === 'number' && value > maxRows) {
                                return;
                            }
                            setMaxRows(value);
                        }}
                        placeholder="Max Rows"
                        className="text-sm border rounded px-2 py-1 w-24"
                    />
                </div>
            </div>

            <div className="flex items-center gap-2">
                <span className="font-medium">Columns:</span>
                <Button size="sm" onClick={handleAddColumn}>
                    + Add Column
                </Button>
            </div>

            {columns.map((col, idx) => (
                <div
                    key={col.id}
                    className="grid grid-cols-5 gap-2 items-start border-b py-2"
                >
                    <input
                        value={col.title}
                        onChange={(e) =>
                            handleColumnChange(idx, { title: e.target.value })
                        }
                        placeholder="Column Title"
                        className="text-sm border rounded px-2 py-1 col-span-2"
                    />

                    <Select
                        value={col.type}
                        onValueChange={(v) =>
                            handleColumnChange(idx, { type: v as PolicyFormTableColumn["type"] })
                        }
                    >
                        <SelectTrigger className="w-28 text-sm">
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                            {QUESTION_TYPES.map(
                                (t) => (
                                    <SelectItem key={t} value={t}>
                                        {t}
                                    </SelectItem>
                                )
                            )}
                        </SelectContent>
                    </Select>

                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveColumn(idx)}
                        className="text-red-500 hover:text-red-700"
                    >
                        <Trash size={14} />
                    </Button>

                    {["select", "radio"].includes(col.type) && (
                        <div className="col-span-5 ml-4 mt-2 space-y-2">
                            {col.options?.map((opt, optIdx) => (
                                <div key={opt.id} className="flex items-center gap-2">
                                    <input
                                        value={opt.id}
                                        onChange={(e) =>
                                            handleColumnOptionChange(idx, optIdx, { id: e.target.value })
                                        }
                                        placeholder="ID"
                                        className="text-xs border rounded px-2 py-1 w-36"
                                    />
                                    <input
                                        value={opt.value}
                                        onChange={(e) =>
                                            handleColumnOptionChange(idx, optIdx, { value: e.target.value })
                                        }
                                        placeholder="Value"
                                        className="text-sm border rounded px-2 py-1 flex-1"
                                    />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveColumnOption(idx, optIdx)}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <Trash size={14} />
                                    </Button>
                                </div>
                            ))}

                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={columnNewOptions[idx]?.id || ""}
                                    onChange={(e) =>
                                        handleColumnNewOptionChange(idx, { id: e.target.value })
                                    }
                                    placeholder="New Option ID"
                                    className="text-xs border rounded px-2 py-1 w-36"
                                />
                                <input
                                    type="text"
                                    value={columnNewOptions[idx]?.value || ""}
                                    onChange={(e) =>
                                        handleColumnNewOptionChange(idx, { value: e.target.value })
                                    }
                                    placeholder="New Option Value"
                                    className="text-sm border rounded px-2 py-1 flex-1"
                                />
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleAddColumnOption(idx)}
                                    className="text-green-600 hover:text-green-700"
                                >
                                    + Add
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};